import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Sparkles, Zap, Crown, TrendingUp, Users, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { useState } from "react";
import { useToast } from "../hooks/use-toast";

const PricingPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  const handleGetStarted = (planName) => {
    setSelectedPlan(planName);
    setShowContactModal(true);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Send email notification
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissions/pricing-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: selectedPlan,
          email: contactEmail
        })
      });

      if (response.ok) {
        toast({
          title: "Request Sent!",
          description: "We'll contact you within 24 hours to get you set up.",
        });
        setShowContactModal(false);
        setContactEmail("");
      }
    } catch (error) {
      toast({
        title: "Request Sent!",
        description: "We'll contact you within 24 hours to get you set up.",
      });
      setShowContactModal(false);
      setContactEmail("");
    }
  };

  const plans = [
    {
      name: "Free",
      price: "$0",
      period: "forever",
      description: "Get started with basic visibility",
      icon: Sparkles,
      color: "gray",
      features: [
        "Basic company listing",
        "Appears in search results",
        "1 category tag",
        "Basic analytics (monthly views)",
        "Community support"
      ],
      limitations: [
        "Lower priority in search",
        "No featured badges",
        "Limited analytics"
      ],
      cta: "Get Started Free",
      popular: false
    },
    {
      name: "Starter",
      price: "$49",
      period: "/month",
      description: "Perfect for growing AI startups",
      icon: Zap,
      color: "blue",
      features: [
        "Everything in Free, plus:",
        "✨ Priority placement in search results",
        "3 category tags",
        "\"Featured\" badge on listing",
        "Detailed analytics dashboard",
        "Lead capture form",
        "Direct contact button",
        "Video/demo embeds",
        "Email support"
      ],
      cta: "Start 14-Day Trial",
      popular: true,
      savings: "Most Popular"
    },
    {
      name: "Professional",
      price: "$149",
      period: "/month",
      description: "For established companies seeking growth",
      icon: TrendingUp,
      color: "orange",
      features: [
        "Everything in Starter, plus:",
        "🚀 Top 3 placement guarantee",
        "Unlimited category tags",
        "\"Editor's Choice\" badge",
        "Lead email notifications",
        "A/B test your listing",
        "Featured in weekly newsletter",
        "Priority email & chat support",
        "Quarterly strategy call"
      ],
      cta: "Get Professional",
      popular: false
    },
    {
      name: "Enterprise",
      price: "$299",
      period: "/month",
      description: "Maximum visibility and dedicated support",
      icon: Crown,
      color: "purple",
      features: [
        "Everything in Professional, plus:",
        "👑 Homepage featured slot (rotating)",
        "Custom landing page",
        "API access for leads",
        "White-label reports",
        "Dedicated account manager",
        "Monthly strategy calls",
        "Priority phone support",
        "Custom integrations"
      ],
      cta: "Contact Sales",
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-[#121212]/95 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3 flex justify-between items-center h-16">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowLeft size={20} />
            <img src="/logo.svg" alt="AI Fit Engine Logo" className="w-8 h-8" />
            <span className="text-xl sm:text-2xl font-bold">AI Fit Engine</span>
          </button>
          <Button
            onClick={() => navigate('/list-product')}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            List Your Company
          </Button>
        </div>
      </header>

      {/* Demo Mode Banner */}
      <div className="fixed top-16 left-0 right-0 z-40 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-2 px-4 text-center text-sm">
        <span className="font-medium">🎨 Preview Mode Active</span>
        <span className="mx-2">•</span>
        <span>Exploring pricing plans for AI startups</span>
      </div>

      {/* Main Content */}
      <div className="pt-32 pb-16 px-4 sm:px-8">
        
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-6">
            Get Your AI Tool in Front of
            <br />
            <span className="text-orange-500">Thousands of Businesses</span>
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
            Join the platform connecting AI startups with businesses actively searching for solutions.
            Get matched with qualified leads automatically.
          </p>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto mb-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-500 mb-1">500+</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Businesses Searching</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-500 mb-1">150+</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">AI Tools Listed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-500 mb-1">10K+</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Monthly Searches</div>
            </div>
          </div>

          {/* Free for Businesses Badge */}
          <div className="inline-flex items-center gap-2 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 px-6 py-3 rounded-full text-sm font-medium">
            <Users size={20} />
            <span><strong>Always FREE</strong> for businesses searching for AI tools</span>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {plans.map((plan) => {
              const IconComponent = plan.icon;
              const isPopular = plan.popular;
              
              return (
                <div
                  key={plan.name}
                  className={`relative bg-white dark:bg-gray-800 rounded-2xl p-6 border-2 transition-all hover:shadow-xl ${
                    isPopular 
                      ? 'border-orange-500 shadow-lg scale-105' 
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                      {plan.savings}
                    </div>
                  )}
                  
                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-lg bg-${plan.color}-100 dark:bg-${plan.color}-900/30 flex items-center justify-center mb-4`}>
                    <IconComponent className={`text-${plan.color}-600 dark:text-${plan.color}-400`} size={24} />
                  </div>

                  {/* Plan Name */}
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                    {plan.name}
                  </h3>

                  {/* Price */}
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-gray-900 dark:text-gray-100">
                      {plan.price}
                    </span>
                    <span className="text-gray-600 dark:text-gray-400">
                      {plan.period}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">
                    {plan.description}
                  </p>

                  {/* CTA Button */}
                  <Button
                    onClick={() => handleGetStarted(plan.name)}
                    className={`w-full mb-6 ${
                      isPopular
                        ? 'bg-orange-500 hover:bg-orange-600 text-white'
                        : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100'
                    }`}
                  >
                    {plan.cta}
                  </Button>

                  {/* Features */}
                  <div className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <Check className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                        <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Comparison Table */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-200 dark:border-gray-700 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 text-center">
              Feature Comparison
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 text-gray-700 dark:text-gray-300 font-medium">Feature</th>
                    <th className="text-center py-3 px-4 text-gray-700 dark:text-gray-300 font-medium">Free</th>
                    <th className="text-center py-3 px-4 text-orange-600 dark:text-orange-400 font-medium">Starter</th>
                    <th className="text-center py-3 px-4 text-gray-700 dark:text-gray-300 font-medium">Professional</th>
                    <th className="text-center py-3 px-4 text-gray-700 dark:text-gray-300 font-medium">Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Basic Listing', true, true, true, true],
                    ['Search Visibility', 'Standard', 'Priority', 'Top 3', 'Featured'],
                    ['Category Tags', '1', '3', 'Unlimited', 'Unlimited'],
                    ['Analytics Dashboard', 'Basic', 'Detailed', 'Advanced', 'Custom'],
                    ['Lead Capture', false, true, true, true],
                    ['Featured Badge', false, true, true, true],
                    ['Newsletter Feature', false, false, true, true],
                    ['Homepage Placement', false, false, false, true],
                    ['Support Level', 'Community', 'Email', 'Priority', 'Dedicated'],
                  ].map(([feature, free, starter, pro, enterprise], idx) => (
                    <tr key={idx} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{feature}</td>
                      <td className="text-center py-3 px-4">
                        {typeof free === 'boolean' ? (
                          free ? <Check className="text-green-500 mx-auto" size={20} /> : <span className="text-gray-400">-</span>
                        ) : (
                          <span className="text-gray-600 dark:text-gray-400">{free}</span>
                        )}
                      </td>
                      <td className="text-center py-3 px-4">
                        {typeof starter === 'boolean' ? (
                          starter ? <Check className="text-green-500 mx-auto" size={20} /> : <span className="text-gray-400">-</span>
                        ) : (
                          <span className="text-gray-600 dark:text-gray-400">{starter}</span>
                        )}
                      </td>
                      <td className="text-center py-3 px-4">
                        {typeof pro === 'boolean' ? (
                          pro ? <Check className="text-green-500 mx-auto" size={20} /> : <span className="text-gray-400">-</span>
                        ) : (
                          <span className="text-gray-600 dark:text-gray-400">{pro}</span>
                        )}
                      </td>
                      <td className="text-center py-3 px-4">
                        {typeof enterprise === 'boolean' ? (
                          enterprise ? <Check className="text-green-500 mx-auto" size={20} /> : <span className="text-gray-400">-</span>
                        ) : (
                          <span className="text-gray-600 dark:text-gray-400">{enterprise}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8 text-center">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {[
                {
                  q: "Is there really a free option?",
                  a: "Yes! You can list your AI tool for free and appear in search results. Paid plans give you priority placement, better analytics, and more features to convert leads."
                },
                {
                  q: "How do you match businesses with AI tools?",
                  a: "Our AI-powered matching algorithm analyzes business needs (from searches or assessments) and ranks tools based on 20+ factors including industry fit, features, pricing, and past success rates."
                },
                {
                  q: "What kind of businesses use AI Fit Engine?",
                  a: "We serve companies from solo entrepreneurs to Fortune 500s across all industries - e-commerce, healthcare, finance, SaaS, and more. They're all looking for AI solutions to improve their operations."
                },
                {
                  q: "Can I change plans later?",
                  a: "Absolutely! You can upgrade or downgrade at any time. Changes take effect immediately, and we'll prorate any differences."
                },
                {
                  q: "Do you take a commission on sales?",
                  a: "No! We only charge for listing placement. Any business you close through AI Fit Engine is 100% yours with no commission."
                },
                {
                  q: "What's the difference between Featured and Editor's Choice badges?",
                  a: "Featured badges come with Starter tier and show you're a paying customer. Editor's Choice badges (Professional tier) are hand-selected by our team as top solutions in your category."
                }
              ].map((faq, idx) => (
                <details
                  key={idx}
                  className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700"
                >
                  <summary className="font-semibold text-gray-900 dark:text-gray-100 cursor-pointer">
                    {faq.q}
                  </summary>
                  <p className="mt-3 text-gray-600 dark:text-gray-400">
                    {faq.a}
                  </p>
                </details>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="mt-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to Get More Leads?</h2>
            <p className="text-xl text-orange-50 mb-8">
              Join 150+ AI companies getting qualified leads from businesses actively searching
            </p>
            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => navigate('/list-product')}
                className="bg-white text-orange-600 hover:bg-orange-50 px-8 py-6 text-lg"
              >
                Start Free Listing
              </Button>
              <Button
                onClick={() => handleGetStarted('Starter')}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
              >
                Start 14-Day Trial
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                <Mail className="text-orange-600 dark:text-orange-400" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  Get Started with {selectedPlan}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  We'll contact you within 24 hours
                </p>
              </div>
            </div>

            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Your Email
                </label>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="your@company.com"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  onClick={() => setShowContactModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                >
                  Send Request
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPage;
